import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { fetchPublishPreview, publishProject } from '@/api/publishes'
import type { ProjectDetail, PublishPreview } from '@/api/types'

const SIXTY_SECONDS = 60 * 1000

export const usePublishPreview = (projectId: string, enabled: boolean = true) =>
  useQuery<PublishPreview>({
    queryKey: ['publishPreview', projectId],
    queryFn: () => fetchPublishPreview(projectId),
    enabled,
    // Recipient count drifts slowly within a session — cache for 60s.
    staleTime: SIXTY_SECONDS,
  })

export const usePublishProject = () => {
  const qc = useQueryClient()
  return useMutation<ProjectDetail, Error, string>({
    mutationFn: (projectId) => publishProject(projectId),
    onSuccess: (project) => {
      // Invalidate everything that renders publish state. Project-detail caches drive the editor
      // header badge; the feed query is invalidated because subscribers' (or our own) views may
      // need to reflect the new entry; preview is invalidated because nextVersion changed.
      qc.setQueryData(['project', project.id], project)
      qc.invalidateQueries({ queryKey: ['project', project.id] })
      qc.invalidateQueries({ queryKey: ['publishPreview', project.id] })
      qc.invalidateQueries({ queryKey: ['feed'] })
      qc.invalidateQueries({ queryKey: ['browse'] })
    },
  })
}
